import { Injectable } from '@angular/core';
import { CedreHttpClient } from '@app/core/http.service';
import {
    Civility,
    IEditUser,
    IUpdateUsersRolesPayload,
    RoleResponse,
    UserProfile,
} from '@app/core/models/user.interface';
import { SNACKBAR_USERS_GET_PROFILE_BY_ID_ERROR } from '@app/shared/utils/snackbar.utils';
import { environment } from '@env/environment';
import {
    ConsentPayload,
    CookieService,
    CustomerProfileExternalId,
    getCookieEntityForApp,
    ProfileService,
    Snackbar,
    User,
} from '@le_cedre/portal-lib';
import Cookie from 'js-cookie';
import { BehaviorSubject, Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { defaultState } from './login.service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    appName$: Observable<string>;
    externalIds$: Observable<CustomerProfileExternalId[]>;

    private _usersToUpdate: any = null;
    usersToUpdate$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    get usersToUpdate(): any {
        return this._usersToUpdate;
    }

    set usersToUpdate(users: any) {
        this._usersToUpdate = users;
        this.usersToUpdate$.next(users);
    }

    constructor(
        private cedreHttpClient: CedreHttpClient,
        private cookieService: CookieService,
        private snackbar: Snackbar,
        private profile: ProfileService
    ) {
        this.appName$ = this.cookieService.appName$;
        this.externalIds$ = this.profile.externalIds$;
    }

    getCivilities(): Promise<Civility[]> {
        return this.cedreHttpClient.get$<Civility[]>(`${environment.urls.v2}hub/session/civilities`);
    }

    getUsers(): Promise<User[]> {
        return this.cedreHttpClient.get$<User[]>(`${environment.urls.v2}hub/session/users`);
    }

    async getProfileById(uId: string): Promise<IEditUser> {
        const idEntity = this.getIdEntity();
        const userProfile = await this.cedreHttpClient
            .get$<UserProfile>(`${environment.urls.v2}hub/session/users/${uId}/entities/${idEntity}`)
            .then((profile) => {
                return profile;
            })
            .catch((error) => {
                console.error(error);
                this.snackbar.createSnackbar({
                    message: SNACKBAR_USERS_GET_PROFILE_BY_ID_ERROR,
                    type: 'danger',
                });
                return null;
            });
        return {
            ...defaultState,
            profile: {
                ...userProfile,
            },
        } as IEditUser;
    }

    getRoles(): Promise<RoleResponse> {
        return this.cedreHttpClient.get$<RoleResponse>(`${environment.urls.v2}lr/roles`);
    }

    async getRolesString(): Promise<string[]> {
        let rolesResponse = await this.getRoles();

        return rolesResponse.data
            .filter((role) => {
                return role.Name.indexOf('CEDRE_') === 0;
            })
            .map((role) => {
                return role.Name;
            }) as string[];
    }

    updateUsersRoles(payload: IUpdateUsersRolesPayload[]) {
        return this.cedreHttpClient.put<IUpdateUsersRolesPayload[]>(
            `${environment.urls.v2}lr/session/permissions`,
            payload
        );
    }

    async postConsent(consents: ConsentPayload[]): Promise<any> {
        const externalIds = await this.externalIds$.pipe(first()).toPromise();
        let datahubSourceId;
        if (externalIds) {
            const dataHubExternalId = (externalIds as any[]).find((el) => el.Source === 'Datahub');
            datahubSourceId = !!dataHubExternalId ? dataHubExternalId.SourceId : null;
        }
        return this.cedreHttpClient.post(
            `${environment.urls.v2}hub/public/users/${datahubSourceId}/consents`,
            consents
        );
    }

    private getIdEntity(): string {
        const COOKIE_ENTITY = JSON.parse(Cookie.get('application'));
        const appName = Cookie.get('id_entity_ef') ?? Cookie.get(getCookieEntityForApp(COOKIE_ENTITY.appName));
        return appName;
    }
}
